import React, { useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import ReactPlayer from "react-player";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";

const streamUrl = process.env.REACT_APP_DEVICE_STREAM_URL || "http://20.121.45.165:8888/mystream/";
const streamMode = (process.env.REACT_APP_DEVICE_STREAM_MODE || "page").toLowerCase();
const backendUrl = process.env.REACT_APP_URL || "";

function LiveStreamPanel() {
  const { siteCode } = useParams();
  const [copyState, setCopyState] = useState("Copy stream URL");
  const [bridgeUrl, setBridgeUrl] = useState("");
  const [bridgeState, setBridgeState] = useState("");
  const [bridgeLoading, setBridgeLoading] = useState(false);
  const sourceUrl = bridgeUrl || streamUrl;
  const isConfigured = Boolean(sourceUrl);
  const isRtsp = streamUrl.toLowerCase().startsWith("rtsp://") && !bridgeUrl;
  const isIframeMode =
    streamMode === "page" ||
    streamMode === "iframe" ||
    (streamMode === "auto" &&
      isConfigured &&
      !isRtsp &&
      !/\.(m3u8|mp4|webm|ogg|ogv|mjpg|mjpeg)(\?|#|$)/i.test(sourceUrl));
  const streamHint = useMemo(() => {
    if (!isConfigured) {
      return "Set REACT_APP_DEVICE_STREAM_URL to the live camera page, HLS stream, or WebRTC feed.";
    }
    if (isRtsp) {
      return "Browsers cannot render RTSP directly. Convert it to HLS or WebRTC, then point this panel to the converted URL.";
    }
    if (isIframeMode) {
      return "This camera source is being embedded as a live browser feed.";
    }
    return "The player below uses the configured browser-safe stream URL.";
  }, [isConfigured, isIframeMode, isRtsp]);

  const copyStreamUrl = async () => {
    if (!sourceUrl || !navigator?.clipboard?.writeText) {
      return;
    }
    await navigator.clipboard.writeText(sourceUrl);
    setCopyState("Copied");
    window.setTimeout(() => setCopyState("Copy stream URL"), 1500);
  };

  const openStream = () => {
    if (sourceUrl && /^https?:\/\//i.test(sourceUrl)) {
      window.open(sourceUrl, "_blank", "noopener,noreferrer");
    }
  };

  const buildBridge = async () => {
    if (!backendUrl || !streamUrl || !streamUrl.toLowerCase().startsWith("rtsp://")) {
      return;
    }

    setBridgeLoading(true);
    setBridgeState("");
    try {
      const response = await axios.post(`${backendUrl}/api/v1/stream/bridge`, {
        rtsp_url: streamUrl,
        site_code: siteCode,
        stream_id: siteCode,
      });
      const playlistUrl = response.data?.data?.playlist_url;
      if (playlistUrl) {
        setBridgeUrl(`${backendUrl}${playlistUrl}`);
        setBridgeState("HLS bridge ready");
      } else {
        setBridgeState("Bridge started");
      }
    } catch (error) {
      console.error("Failed to build HLS bridge", error);
      setBridgeState("Bridge failed");
    } finally {
      setBridgeLoading(false);
    }
  };

  return (
    <div
      className="shadow-sm rounded-4 p-3 h-100"
      style={{
        background: "linear-gradient(180deg, #0f172a 0%, #111827 100%)",
        color: "#fff",
        minHeight: 520,
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <div className="text-uppercase" style={{ letterSpacing: 1.2, fontSize: 12, color: "#94a3b8" }}>
            Live Stream
          </div>
          <h4 className="mb-1 fw-bold">Live Camera</h4>
          <div style={{ color: "#cbd5e1" }}>Live view from the emergency call box camera.</div>
        </div>
        <Chip
          icon={<VideocamOutlinedIcon />}
          label={isConfigured ? "Camera online" : "Not set"}
          color={isConfigured ? "success" : "default"}
          variant="filled"
          sx={{ backgroundColor: isConfigured ? "#16a34a" : "#334155", color: "#fff" }}
        />
      </div>

      <div
        style={{
          borderRadius: 18,
          overflow: "hidden",
          background: "#020617",
          border: "1px solid rgba(148, 163, 184, 0.18)",
          minHeight: 360,
          }}
        >
        {isConfigured && isIframeMode ? (
          <iframe
            title="Live camera feed"
            src={sourceUrl}
            width="100%"
            height="360"
            style={{ border: 0, display: "block", background: "#020617" }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        ) : isConfigured && !isRtsp ? (
          <ReactPlayer
            url={sourceUrl}
            playing
            controls
            muted
            width="100%"
            height="360px"
            style={{ background: "#020617" }}
          />
        ) : (
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center p-4"
            style={{ minHeight: 360 }}
          >
            <PlayCircleOutlineIcon sx={{ fontSize: 70, color: "#94a3b8", mb: 2 }} />
            <h5 className="mb-2">{isRtsp ? "RTSP bridge required" : "Live feed not connected yet"}</h5>
            <div style={{ maxWidth: 560, color: "#cbd5e1" }}>{streamHint}</div>

            {isRtsp ? (
              <div
                className="w-100 mt-3 p-3 text-start"
                style={{
                  maxWidth: 720,
                  borderRadius: 16,
                  background: "rgba(15, 23, 42, 0.72)",
                  border: "1px solid rgba(148, 163, 184, 0.2)",
                  color: "#e2e8f0",
                }}
              >
                <div className="small text-uppercase mb-2" style={{ color: "#94a3b8", letterSpacing: 1.1 }}>
                  Suggested bridge
                </div>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    color: "#e2e8f0",
                    fontFamily: "JetBrains Mono, Consolas, monospace",
                    fontSize: 12,
                  }}
                >
{`ffmpeg -fflags +genpts -re -i "${streamUrl || "rtsp://<camera-ip>/stream"}" -c:v copy -f hls -hls_time 2 -hls_list_size 6 -hls_flags delete_segments ./public/device.m3u8`}
                </pre>
              </div>
            ) : isIframeMode ? (
              <div
                className="w-100 mt-3 p-3 text-start"
                style={{
                  maxWidth: 720,
                  borderRadius: 16,
                  background: "rgba(15, 23, 42, 0.72)",
                  border: "1px solid rgba(148, 163, 184, 0.2)",
                  color: "#e2e8f0",
                }}
              >
                <div className="small text-uppercase mb-2" style={{ color: "#94a3b8", letterSpacing: 1.1 }}>
                  Embedded source
                </div>
                <div style={{ color: "#cbd5e1" }}>
                  The configured URL looks like a browser page, so it is embedded directly above.
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
        <Chip
          label={isConfigured ? "Live feed ready" : "Waiting for camera URL"}
          variant="outlined"
          sx={{ borderColor: "#475569", color: "#e2e8f0" }}
        />
        {bridgeState && (
          <Chip
            label={bridgeState}
            variant="filled"
            color={bridgeState === "HLS bridge ready" ? "success" : "default"}
            sx={{ backgroundColor: bridgeState === "HLS bridge ready" ? "#16a34a" : "#334155", color: "#fff" }}
          />
        )}
        {isConfigured && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyStreamUrl}
            sx={{ borderColor: "#475569", color: "#e2e8f0" }}
          >
            {copyState}
          </Button>
        )}
        {isConfigured && /^https?:\/\//i.test(sourceUrl) && !isRtsp && (
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={openStream}
            sx={{ backgroundColor: "#2563eb" }}
          >
            Open stream
          </Button>
        )}
        {isRtsp && (
          <Button
            size="small"
            variant="contained"
            onClick={buildBridge}
            disabled={bridgeLoading}
            sx={{ backgroundColor: "#f97316" }}
          >
            {bridgeLoading ? "Building bridge..." : "Build HLS bridge"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default LiveStreamPanel;
